import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase.config";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Card,
    Fade,
    Alert
} from "@mui/material";

import { useParams } from "react-router-dom";
import Iconify from "../Components/Iconify";
import { daysInWeek } from "date-fns";
import AnimatedCountdown from "../Components/countdow/AnimatedCountdown";

export default function WaitingRoomEncrypted() {
    const { encryptedValue } = useParams();

    const [eventId, setEventId] = useState(null);
    const [ticketId, setTicketId] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [canJoin, setCanJoin] = useState(false);
    const [countdown, setCountdown] = useState("");
    const [tokenError, setTokenError] = useState(false);

    // -------------------------------
    // 1. Décryptage du token
    // -------------------------------
    function decodeToken(base64UrlString) {
        try {
            // Base64URL → Base64
            const base64 = base64UrlString.replace(/-/g, "+").replace(/_/g, "/");

            // decode en string
            const jsonString = atob(base64);

            // parse en objet
            return JSON.parse(jsonString);
        } catch (err) {
            console.error("Invalid encrypted token:", err);
            return null;
        }
    }

    useEffect(() => {
        const decoded = decodeToken(encryptedValue);

        if (!decoded || !decoded.eventId || !decoded.ticketId) {
            setTokenError(true);
            setLoading(false);
            return;
        }

        setEventId(decoded.eventId);
        setTicketId(decoded.ticketId);
    }, [encryptedValue]);


    // -------------------------------
    // 2. Chargement de l'événement si token valide
    // -------------------------------
    useEffect(() => {
        if (!eventId) return;

        const fetchEvent = async () => {
            try {
                const eventRef = doc(db, "evenement", eventId);
                const snap = await getDoc(eventRef);

                if (snap.exists()) {
                    const data = snap?.data();
                    setEventData(data);
                    checkJoinTime(data?.dateDebut.toDate());
                } else {
                    setTokenError(true);
                }
            } catch (err) {
                setTokenError(true);
            }

            setLoading(false);
        };

        fetchEvent();
    }, [eventId]);

    // -------------------------------
    // 3. Gestion du compte à rebours
    // -------------------------------
    const checkJoinTime = (startDate) => {
        const now = new Date();
        const openTime = new Date(startDate.getTime() - 15 * 60 * 1000);

        if (now >= openTime) {
            setCanJoin(true);
        } else {
            const diff = openTime - now;
            startCountdown(diff);
        }
    };

    const startCountdown = (milliseconds) => {
        let timeLeft = milliseconds;

        const interval = setInterval(() => {
            timeLeft -= 1000;

            if (timeLeft <= 0) {
                clearInterval(interval);
                setCanJoin(true);
                return;
            }

            // Calcul
            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor(
                (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
            );
            const minutes = Math.floor(
                (timeLeft % (1000 * 60 * 60)) / (1000 * 60)
            );
            const seconds = Math.floor(
                (timeLeft % (1000 * 60)) / 1000
            );

            // Format HH:MM:SS
            const timeFormatted =
                `${String(hours).padStart(2, "0")}:` +
                `${String(minutes).padStart(2, "0")}:` +
                `${String(seconds).padStart(2, "0")}`;

            // Si plus d’un jour → afficher "Xj HH:MM:SS"
            const formatted = days > 0
                ? `${days}j ${timeFormatted}`
                : timeFormatted;

            setCountdown({
                days,
                hours,
                minutes,
                seconds
            });

        }, 1000);
    };



    // -------------------------------
    // 4. Rejoindre l'événement
    // -------------------------------
    const handleJoin = async () => {
        await updateDoc(doc(db, "bills", ticketId), {
            joined: true,
            joinedAt: new Date()
        });

        window.location.href = eventData.meetingUrl;
    };

    // -------------------------------
    // 5. Gestion des erreurs de token
    // -------------------------------

    if (tokenError) {
        return (
            <Box sx={{ pt: 10, mt: 5, paddingX: 2 }}>
                <Alert severity="error">
                    Lien invalide ou expiré.
                    Veuillez vérifier votre invitation.
                </Alert>
            </Box>
        );
    }

    /*
      if (loading || !eventData) {
        return (
          <Box sx={{ height:'100vh', pt: 20, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        );
      }
      */

    // ----------------------------------------------------
    // 📌 UI Premium Choptonticket avec image d’événement
    // ----------------------------------------------------
    return (
        <Fade in>
            <Box
                sx={{
                    paddingTop: 10,
                    minHeight: "100vh",
                    bgcolor: "#F4F0FE",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2
                }}
            >
                <Card
                    sx={{
                        maxWidth: 450,
                        width: "100%",
                        pb: 4,
                        borderRadius: "24px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                        textAlign: "center"
                    }}
                >

                    {/* IMAGE DE L’ÉVÈNEMENT */}
                    <Box
                        sx={{
                            height: 200,
                            width: "100%",
                            backgroundImage: `url(${eventData?.idVisuel})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            position: "relative"
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                bgcolor: "rgba(0,0,0,0.30)"
                            }}
                        />
                    </Box>

                    <Box sx={{ p: 3 }}>


                        <Typography variant="h6" fontWeight={700} mt={2}>
                            {eventData?.name}
                        </Typography>

                        <Typography color="text.secondary" mt={1}>
                            L’événement commence à{" "}
                            <strong>
                                {eventData?.startDate?.toDate()?.toLocaleString("fr-FR", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </strong>
                        </Typography>

                        {/* COUNTDOWN */}
                        {!canJoin ? (
                            <>
                                <Box
                                    sx={{
                                        mt: 4,
                                        mb: 2,
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: "#F8F6FF"
                                    }}
                                >
                                    <Typography color="text.secondary" mb={1}>
                                        L’accès ouvrira dans :
                                    </Typography>
                                    <AnimatedCountdown
  days={countdown.days}
  hours={countdown.hours}
  minutes={countdown.minutes}
  seconds={countdown.seconds}
/>

                                    
                                </Box>

                                <Typography color="gray">Merci de patienter…</Typography>
                            </>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleJoin}
                                sx={{
                                    mt: 4,
                                    py: 1.6,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: "14px",
                                    background: "linear-gradient(135deg, #6A4AFF, #B57BFF)",
                                    boxShadow: "0 6px 20px rgba(106,74,255,0.3)"
                                }}
                                fullWidth
                            >
                                Rejoindre l’événement
                            </Button>
                        )}
                    </Box>
                </Card>
            </Box>
        </Fade>
    );
}
